import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateTask } from '../hooks/mutations';
import { useDateTime } from '../contexts/DateTimeContext';
import { useTask } from '../contexts/TaskContext';
import { PRIORITIES, extractPlainText } from '../lib/constants';
import { statusValueName } from '../lib/statusName';
import InlineTaskRow from './InlineTaskRow';
import ProjectChip from './ProjectChip';
import UnreadBadge from './unread/UnreadBadge';
import { useUnreadsQuery, useTasksQuery } from '../hooks/queries';
import { SpotlightMark, useSpotlightMarks } from './spotlight/SpotlightMenu';
import { nestTasks, subtaskProgress } from '../lib/subtasks';
import { getDefaultFields } from '../lib/viewFields';
import { NO_PROJECT, shownProjectId } from '../lib/taskFilterEngine';
import { useMediaQuery, SCREEN } from '../hooks/useMediaQuery';
import {
  CheckCircle2, Circle, ChevronDown, ChevronRight, Clock, Paperclip,
  CalendarClock, Tag, User
} from 'lucide-react';

const STATUS_COLORS = {
  todo: 'bg-gray-500/15 text-muted-foreground',
  in_progress: 'bg-blue-500/15 text-blue-400',
  completed: 'bg-green-500/15 text-green-500',
  archived: 'bg-zinc-500/15 text-zinc-400',
};

function renderCustomFieldValue(def, raw, lang) {
  if (raw == null || raw === '') return null;
  if (def.field_type === 'checkbox') return raw ? '✓' : null;
  if (def.field_type === 'multi_select' && Array.isArray(raw)) {
    const labels = raw.map(id => def.options?.find(o => o.id === id)?.label).filter(Boolean);
    return labels.join(', ') || null;
  }
  if (def.field_type === 'select') {
    return def.options?.find(o => o.id === raw)?.label || null;
  }
  if (def.field_type === 'date') {
    try { return new Date(raw).toLocaleDateString(lang); } catch { return String(raw); }
  }
  return String(raw);
}

/**
 * ListView - A simple, Todoist-style list view for tasks.
 * Lighter than TasksTable - just checkbox, title, and inline chips.
 */
const ListView = ({
  tasks = [],
  projects = [],
  onUpdate,
  onTaskClick,
  prefillProjectId = null,
  // New tasks from the quick-add row go to this list of the project.
  prefillSectionId = null,
  teamId = null,
  groupBy = 'none', // none, project, priority, status
  visibleFields = null,
  customFieldDefs = [],
  readOnly = false,
  // New tasks from the quick-add row become subtasks of this task (the task detail).
  parentId = null,
  // "No tasks" under an empty list; a project's lists show just their quick-add row.
  showEmpty = true,
}) => {
  // One hook call for the whole list: rows are produced in a .map(), so each
  // row reads from this shared snapshot instead of calling a hook of its own.
  const { data: unreads } = useUnreadsQuery();
  const hasUnread = (id) => Boolean(unreads?.tasks?.[id]);
  const spotlight = useSpotlightMarks();  // marks on cards: in Spotlight, or put it there

  const fields = useMemo(() => {
    if (Array.isArray(visibleFields)) return new Set(visibleFields);
    return new Set([...getDefaultFields('list'), ...customFieldDefs.map(d => `cf_${d.id}`)]);
  }, [visibleFields, customFieldDefs]);
  const show = (key) => fields.has(key);
  const { t, i18n } = useTranslation();
  // Phones get the subtitle under the title, wider screens the chips; only one is drawn.
  const wide = useMediaQuery(SCREEN.md);
  const { formatDate } = useDateTime();
  // Known status values in the app's language; any other value shows as stored.
  const statusLabel = (status) => (!status || STATUS_COLORS[status] ? statusValueName(status || 'todo', t) : status);
  const { openTaskDetail } = useTask();
  const updateTask = useUpdateTask();
  const [expandedGroups, setExpandedGroups] = useState({});
  // Subtasks show under their parent when it is in the list (lib/subtasks.js); a
  // subtask on its own shows its parent's title, which may be in no list here.
  const [collapsedParents, setCollapsedParents] = useState(() => new Set());
  const toggleSubtasks = (taskId) => setCollapsedParents((prev) => {
    const next = new Set(prev);
    if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
    return next;
  });
  const { data: allTasks } = useTasksQuery({}, { enabled: !readOnly });
  const taskTitle = (id) => tasks.find((x) => x.id === id)?.title || allTasks?.find((x) => x.id === id)?.title;

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: prev[key] === false ? true : false }));
  };

  const getProjectById = (id) => projects.find(p => p.id === id);

  const grouped = useMemo(() => {
    if (groupBy === 'none') return [{ key: 'all', label: '', tasks }];

    const groups = {};
    tasks.forEach(task => {
      let key, label;
      if (groupBy === 'priority') {
        key = task.priority || 'p4';
        label = PRIORITIES[key] ? `${PRIORITIES[key].shortLabel} ${t(PRIORITIES[key].labelKey)}` : t('bulk_action_bar.priority_low');
      } else if (groupBy === 'project') {
        // One "No Project" group: a task of a trashed project has none here either.
        key = shownProjectId(task, projects) || NO_PROJECT;
        label = projects.find(pr => pr.id === key)?.name || t('common.no_project');
      } else {
        key = task.status || 'todo';
        label = STATUS_COLORS[key] ? statusValueName(key, t) : key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      }
      if (!groups[key]) groups[key] = { key, label, tasks: [] };
      groups[key].tasks.push(task);
    });
    return Object.values(groups);
  }, [tasks, groupBy, projects, t]);

  const handleTaskClick = (task) => {
    if (readOnly) return;
    if (onTaskClick) {
      onTaskClick(task);
    } else {
      openTaskDetail(task.id, { teamId });
    }
  };

  const handleToggleComplete = async (e, task) => {
    e.stopPropagation();
    if (readOnly) return;
    const isCompleted = task.status === 'completed';
    // useUpdateTask runs an optimistic cache patch in onMutate and the axios
    // interceptor invalidates the task list keys on success; no need to fire
    // any extra event or call onUpdate.
    try {
      await updateTask.mutateAsync({
        id: task.id,
        data: { status: isCompleted ? 'todo' : 'completed' },
      });
    } catch (err) {
      console.error('Failed to toggle task completion:', err);
    }
    onUpdate?.();
  };

  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const taskDate = new Date(date);
    taskDate.setHours(0, 0, 0, 0);
    const diffDays = Math.round((taskDate - today) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return { label: t('common.today'), className: 'text-amber-700 dark:text-amber-400' };
    if (diffDays === 1) return { label: t('common.tomorrow'), className: 'text-blue-400' };
    if (diffDays < 0) return { label: formatDate(date), className: 'text-red-400' };
    return { label: formatDate(date), className: 'text-muted-foreground' };
  };

  return (
    <div className="space-y-1">
      {grouped.map(group => {
        const isExpanded = expandedGroups[group.key] !== false;
        return (
          <div key={group.key}>
            {/* Group header */}
            {groupBy !== 'none' && (
              <button
                onClick={() => toggleGroup(group.key)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent/60 transition-colors rounded-lg"
              >
                {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                <span className="font-medium text-foreground text-sm">{group.label}</span>
                <span className="text-xs text-muted-foreground">({group.tasks.length})</span>
              </button>
            )}

            {/* Tasks */}
            {isExpanded && (
              <div className="space-y-0.5">
                {nestTasks(group.tasks, collapsedParents).map(({ task, depth, childCount }) => {
                  const priority = PRIORITIES[task.priority] || PRIORITIES.p4;
                  const progress = subtaskProgress(task);
                  const parentTitle = task.parent_id && depth === 0 && task.parent_id !== parentId
                    ? taskTitle(task.parent_id) : null;
                  const isCompleted = task.status === 'completed';
                  const dueInfo = formatDueDate(task.due_date);
                  const project = getProjectById(task.project_id);

                  return (
                    <div
                      key={task.id}
                      onClick={() => handleTaskClick(task)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-accent/60 transition-colors group ${readOnly ? '' : 'cursor-pointer'}`}
                      style={depth ? { paddingLeft: 12 + depth * 24 } : undefined}
                    >
                      {/* Checkbox - hidden in read-only since toggling is disabled */}
                      {!readOnly && (
                        <button
                          onClick={(e) => handleToggleComplete(e, task)}
                          className="shrink-0"
                        >
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                          ) : (
                            <Circle className={`w-5 h-5 ${priority.textColor} opacity-50 group-hover:opacity-100`} />
                          )}
                        </button>
                      )}

                      {/* Title + optional description */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 min-w-0">
                          <div className={`text-[15px] md:text-sm truncate ${isCompleted ? 'line-through text-muted-foreground/70' : 'text-foreground'} ${hasUnread(task.id) ? 'font-semibold' : ''}`}>
                            {task.title}
                          </div>
                          <SpotlightMark taskId={task.id} mark={spotlight.items[task.id]} sections={spotlight.sections} addable={!readOnly} />
                        </div>
                        {parentTitle && (
                          <div className="text-xs text-muted-foreground truncate mt-0.5">{parentTitle}</div>
                        )}
                        {show('description') && (
                          <div className="text-xs text-muted-foreground/70 truncate mt-0.5">
                            {extractPlainText(task.description) || '-'}
                          </div>
                        )}
                        {/* Mobile subtitle: subtle gray meta (date + status), no pills */}
                        {!wide && <div className="md:hidden mt-0.5 flex items-center gap-1.5 text-[12px]">
                          {dueInfo?.label && (
                            <span className={dueInfo.className?.includes('red') ? 'text-red-500' : dueInfo.className?.includes('amber') ? 'text-amber-500' : 'text-muted-foreground'}>
                              {dueInfo.label}
                            </span>
                          )}
                          {task.status && task.status !== 'todo' && task.status !== 'to_do' && (
                            <>
                              {dueInfo?.label && <span className="text-muted-foreground/40">·</span>}
                              <span className="text-muted-foreground">{statusLabel(task.status)}</span>
                            </>
                          )}
                          {project && (
                            <>
                              {(dueInfo?.label || (task.status && task.status !== 'todo')) && <span className="text-muted-foreground/40">·</span>}
                              <ProjectChip
                                project={project}
                                dotSize="w-1.5 h-1.5"
                                textClass="text-muted-foreground max-w-[180px]"
                              />
                            </>
                          )}
                        </div>}
                      </div>

                      {/* Inline chips — desktop only. Mobile renders a subtle subtitle above instead. */}
                      {wide && <div className="hidden md:flex items-center gap-2 shrink-0 flex-wrap justify-end">
                        {(progress || childCount > 0) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); if (childCount > 0) toggleSubtasks(task.id); }}
                            disabled={childCount === 0}
                            className="flex items-center gap-0.5 text-xs text-muted-foreground/70 hover:text-foreground disabled:hover:text-muted-foreground/70 tabular-nums"
                            title={t('tasks.subtasks')}
                            aria-label={t('tasks.subtasks')}
                            aria-expanded={childCount > 0 ? !collapsedParents.has(task.id) : undefined}
                          >
                            {childCount > 0 && !collapsedParents.has(task.id) ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            {progress ? `${progress.done}/${progress.total}` : childCount}
                          </button>
                        )}
                        {show('status') && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[task.status] || STATUS_COLORS.todo}`}>
                            {statusLabel(task.status)}
                          </span>
                        )}
                        {show('duration') && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground/70">
                            <Clock className="w-3 h-3" />
                            {task.duration
                              ? (task.duration >= 60 ? t('ui_task.duration.hours', { h: Math.floor(task.duration / 60) }) : t('ui_task.duration.minutes', { m: task.duration }))
                              : '-'}
                          </span>
                        )}
                        {show('project') && groupBy !== 'project' && (
                          <span className="text-xs text-muted-foreground/70">
                            {project
                              ? <ProjectChip project={project} />
                              : <span className="text-muted-foreground/50">-</span>}
                          </span>
                        )}
                        {show('priority') && (
                          <span className={`text-xs font-medium ${priority.textColor}`}>
                            {priority.shortLabel}
                          </span>
                        )}
                        {show('start_date') && (
                          <span className="text-xs text-muted-foreground/70 flex items-center gap-1">
                            <CalendarClock className="w-3 h-3" />
                            {task.start_date ? formatDate(new Date(task.start_date)) : '-'}
                          </span>
                        )}
                        {show('due_date') && (
                          <span className={`text-xs ${dueInfo?.className || 'text-muted-foreground/50'}`}>
                            {dueInfo?.label || '-'}
                          </span>
                        )}
                        {show('assignee') && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground/70">
                            <User className="w-3 h-3" />
                            {task.assigned_to?.length || 0}
                          </span>
                        )}
                        {show('labels') && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground/70">
                            <Tag className="w-3 h-3" />
                            {task.label_ids?.length || 0}
                          </span>
                        )}
                        <UnreadBadge taskId={task.id} />
                        {show('attachments') && (
                          <span className="flex items-center gap-0.5 text-xs text-muted-foreground/70">
                            <Paperclip className="w-3 h-3" />
                            {task.attachments?.length || 0}
                          </span>
                        )}
                        {/* Custom fields chip row — hidden on mobile to reduce clutter.
                            Tap the task to see all fields in the detail panel. */}
                        <div className="hidden md:contents">
                          {customFieldDefs.map(def => {
                            if (!fields.has(`cf_${def.id}`)) return null;
                            const val = renderCustomFieldValue(def, task.custom_fields?.[def.id], i18n.language);
                            if (val == null || val === '') return null;
                            return (
                              <span key={def.id} className="text-xs text-muted-foreground/70 flex items-center gap-1">
                                <span className="text-muted-foreground/70">{def.name}:</span>
                                <span className="truncate max-w-[120px]">{val}</span>
                              </span>
                            );
                          })}
                        </div>
                      </div>}
                    </div>
                  );
                })}

                {/* Inline quick-add - hidden in read-only mode (public viewer) */}
                {!readOnly && (
                  <InlineTaskRow
                    projectId={prefillProjectId || (groupBy === 'project' && group.key !== NO_PROJECT ? group.key : null)}
                    sectionId={prefillSectionId}
                    teamId={teamId}
                    parentId={parentId}
                    placeholder={parentId ? t('tasks.add_subtask') : null}
                    onTaskCreated={onUpdate}
                    className="ml-3"
                  />
                )}
              </div>
            )}
          </div>
        );
      })}

      {tasks.length === 0 && !parentId && showEmpty && (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-sm">{t('tasks.no_tasks')}</p>
          <p className="text-xs mt-1">{t('tasks.no_tasks_description')}</p>
        </div>
      )}
    </div>
  );
};

// Drawn again only when its props change, not with every refresh of the page's queries.
export default React.memo(ListView);
